import { client } from '@api/client';
import { URL } from '@api/urls';

import { EConfigurationDataFolderFieldNames } from '@interfaces/cmModule/configurationData';
import {
	ICreateSchemaDTO,
	IExportSchemaDTO,
	TAnalysisSchemaDTO,
} from '@interfaces/cmModule/dto/schemaConfigDTO';
import {
	IAnalysisSchemaUrl,
	IClearSchemaDTO,
	IDeleteOranSchemaDTO,
	IDeleteSchemaDTO,
	IOranValuesForCreateUrl,
	IRebuildSchemaDTO,
	ISchemaConfigResponseType,
	ISchemaConfigUploadUrl,
	IValuesForCreateUrl,
	IViewSchemaConfigUrl,
} from '@interfaces/cmModule/schemaConfig';
import { TNameTitle } from '@interfaces/common/common';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';

const {
	schemaConfig,
	schemaOranConfig,
} = URL;

export const getSchemaConfig = (params: TDefaultDTO): Promise<ISchemaConfigResponseType> => {
	return client.get(schemaConfig, {
		params,
	});
};

export const getOranSchemaConfig = (params: TDefaultDTO): Promise<ISchemaConfigResponseType> => {
	const isFolder = params.sortFieldName === EConfigurationDataFolderFieldNames.PRESET_NAME;

	return client.get(schemaOranConfig, {
		params: {
			...params,
			sortFieldName: isFolder ? params.sortFieldName : null,
			sortDirection: isFolder ? params.sortDirection : null,
			fileSortFieldName: isFolder ? null : params.sortFieldName,
			fileSortDirection: isFolder ? null : params.sortDirection,
		},
	});
};

export const exportSchemaConfigs = (dto: IExportSchemaDTO): Promise<void> => {
	return client.post(`${schemaConfig}/export`, dto);
};

export const exportOranSchemaConfigs = (dto: IExportSchemaDTO): Promise<void> => {
	return client.post(`${schemaOranConfig}/export`, dto);
};

export const uploadSchema = (fileName: string): Promise<ISchemaConfigUploadUrl> => {
	return client.post(`${schemaConfig}/create-upload-url`, { fileName });
};

export const uploadOranSchema = (fileName: string): Promise<ISchemaConfigUploadUrl> => {
	return client.post(`${schemaOranConfig}/create-upload-url`, { fileName });
};

export const viewSchemaConfig = (id: string): Promise<IViewSchemaConfigUrl> => {
	return client.post(`${schemaConfig}/${id}/file-data`, {});
};

export const viewOranSchemaConfig = (id: string): Promise<IViewSchemaConfigUrl> => {
	return client.get(`${schemaOranConfig}/${id}/file-data`);
};

export const analysisFiles = (data: TAnalysisSchemaDTO): Promise<IAnalysisSchemaUrl> => {
	return client.post(`${schemaConfig}/analysis-files`, { ...data });
};

export const analysisOranFiles = (data: TAnalysisSchemaDTO): Promise<IAnalysisSchemaUrl> => {
	return client.post(`${schemaOranConfig}/analysis-files`, { ...data });
};

export const deleteSchema = (data: IDeleteSchemaDTO): Promise<void> => {
	return client.post(`${schemaConfig}/delete`, data);
};

export const clearSchema = ({ clearFolderId }: IClearSchemaDTO): Promise<void> => {
	return client.post(`${schemaOranConfig}/${clearFolderId}/clear`, {});
};

export const rebuildSchema = ({ folderId }: IRebuildSchemaDTO): Promise<void> => {
	return client.post(`${schemaOranConfig}/${folderId}/rebuild`, {});
};

export const deleteOranSchema = (data: IDeleteOranSchemaDTO): Promise<void> => {
	return client.post(`${schemaOranConfig}/delete`, data);
};

export const createMavenirConfigRecord = (data: ICreateSchemaDTO): Promise<void> => {
	return client.post(schemaConfig, { ...data });
};

export const createOranConfigRecord = (data: ICreateSchemaDTO): Promise<void> => {
	return client.post(schemaOranConfig, { ...data });
};

export const createOranConfigRecords = (data: ICreateSchemaDTO[]): Promise<void[]> => {
	return Promise.all(data.map((item) => createOranConfigRecord(item)));
};

export const getValuesForCreate = (): Promise<IValuesForCreateUrl> => {
	return client.get(`${schemaConfig}/values-for-create`);
};

export const getOranValuesForCreate = (): Promise<IOranValuesForCreateUrl> => {
	return client.get(`${schemaOranConfig}/values-for-create`);
};

export const getVendorsForSchema = (): Promise<TNameTitle[]> => {
	return client.get(`${schemaOranConfig}/vendors`);
};

export const applyConfigsSchema = (): Promise<void> => {
	return client.post(`${schemaConfig}/apply-configs`, {});
};

export const clearConfigsSchema = (): Promise<void> => {
	return client.post(`${schemaConfig}/clear-configs`, {});
};
